import {
  GAME_ID,
  REDUX_ACTIONS,
  GAME_MODES,
  ITEMS_PER_ROUND,
  QUESTIONS_PER_ROUND,
  SCREENS,
  QUESTION_TYPES,
  INTRO_BUBBLE_TEXT,
} from '../../data/constants';
import { getLevelIntroTextsCount } from '../selectors/curriculumSelectors';

const initState = {
  levelIndex: 0,
  levelAfterOnboarding: -1, // -1 means no level is set after onboarding
  questionIndex: 0,
  introTextIndex: 0,
  introBubbleText: '',
  articlesInRound: [],
  crowd: {},
  questionType: QUESTION_TYPES.INTEGRATION,
  gameMode: GAME_MODES.CLAIM_SELECTION,
  currentArticle: '', // id of current article (one testimonial and claims)
  selectedClaimIds: [], // claim ids that were selected during round
  selectedEvidences: [], // evidences texts that were selected during round
  isCorrectEvidenceFeedback: false,
  currentSelectedEvidenceIndexes: [], // ids of evidences that are being selecting during EVIDENCE_SELECTION game mode
  isTextIntroPhase: false,
  isTextScrollerAtTheBottom: false,
  answeredCharacters: [],
  viewedArticles: [],
  correctAnswersCount: 0,
  answeredQuestionsCount: 0,
  attemptCount: 0,
  selectedResponse: null,
  disabledPhrases: [],
  selectedIntegrationOptionIndex: null,
  failedQuestionsCount: 0,
  records: [],
};

const roundPlayReducer = (state = initState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  let answeredCharacters = [];
  let viewedArticles = [];
  switch (type) {
    case REDUX_ACTIONS.SET_LEVEL_INDEX:
      return {
        ...state,
        levelIndex: payload,
      };

    case REDUX_ACTIONS.SET_LEVEL_AFTER_ONBOARDING:
      return {
        ...state,
        levelAfterOnboarding: payload,
      };

    case REDUX_ACTIONS.INITIALIZE_ROUND:
      return {
        ...state,
        introBubbleText: INTRO_BUBBLE_TEXT.BEGINNING_OF_LEVEL,
        articlesInRound: payload.articlesInRound,
        questionIndex: 0,
        currentArticle: payload.articlesInRound[0],
        isTextIntroPhase: true,
        selectedClaimIds: [],
        selectedEvidences: [],
        isCorrectEvidenceFeedback: false,
        isTextScrollerAtTheBottom: false,
        introTextIndex: 0,
        answeredCharacters: [],
        viewedArticles: [],
        attemptCount: 0,
        selectedResponse: null,
        disabledPhrases: [],
        selectedIntegrationOptionIndex: null,
        records: [],
      };

    case REDUX_ACTIONS.INITIALIZE_CROWD:
      return {
        ...state,
        crowd: payload.crowd,
      };

    case REDUX_ACTIONS.LOAD_LOCALLY_SAVED_LEVEL: {
      const savedState = payload.roundPlay ? payload.roundPlay : {};
      // peeling out crowd info if present so new shuffled positions persists
      const { crowd, ...withoutCrowd } = savedState;

      // Reset introTextIndex only if it's out of bounds for the current level
      // This prevents crashes while preserving user progress when returning to CROWD screen
      let introTextIndex = savedState.introTextIndex ?? 0;
      if (payload.screen?.currentScreen === SCREENS.CROWD && savedState.levelIndex !== undefined) {
        const maxIntroTextIndex = getLevelIntroTextsCount(savedState.levelIndex) - 1;
        if (introTextIndex > maxIntroTextIndex) {
          introTextIndex = 0;
        }
      }

      return {
        ...state,
        ...withoutCrowd,
        introTextIndex,
      };
    }

    case REDUX_ACTIONS.SET_CURRENT_ARTICLE:
      answeredCharacters = state.answeredCharacters.slice();
      viewedArticles = state.viewedArticles.slice();
      answeredCharacters.push(payload.characterId);
      viewedArticles.push(payload.articleId);
      return {
        ...state,
        introTextIndex: state.introTextIndex + 1,
        currentArticle: payload.articleId,
        answeredCharacters,
        viewedArticles,
      };

    case REDUX_ACTIONS.ADD_SELECTED_CLAIM_ID:
      return {
        ...state,
        selectedClaimIds: [...state.selectedClaimIds, payload],
      };

    case REDUX_ACTIONS.ADD_SELECTED_EVIDENCE:
      return {
        ...state,
        selectedEvidences: [...state.selectedEvidences, payload],
      };

    case REDUX_ACTIONS.CORRECT_EVIDENCE_FEEDBACK:
      return {
        ...state,
        isCorrectEvidenceFeedback: payload,
      };

    case REDUX_ACTIONS.INCREMENT_INTRO_TEXT:
      return {
        ...state,
        introTextIndex: state.introTextIndex + 1,
      };

    case REDUX_ACTIONS.UPDATE_RECORDS:
      return {
        ...state,
        records: [...state.records, payload],
      };

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM: {
      const gameMode = state.answeredQuestionsCount % 2 !== 0 ?
        GAME_MODES.EVIDENCE_SELECTION :
        GAME_MODES.CLAIM_SELECTION;
      let { isTextIntroPhase, currentSelectedEvidenceIndexes } = state;
      if (state.answeredQuestionsCount % 2 === 0) {
        isTextIntroPhase = true;
        currentSelectedEvidenceIndexes = [];
      }
      return {
        ...state,
        questionIndex: Math.min(state.questionIndex + 1, ITEMS_PER_ROUND - 1),
        gameMode,
        isTextIntroPhase,
        currentSelectedEvidenceIndexes,
        attemptCount: 0,
        selectedResponse: null,
        disabledPhrases: [],
      };
    }

    case REDUX_ACTIONS.TOGGLE_EVIDENCE_INDEXES: {
      const currentSelectedEvidenceIndexes = state.currentSelectedEvidenceIndexes.includes(payload) ?
        state.currentSelectedEvidenceIndexes.filter(item => item !== payload) :
        [...state.currentSelectedEvidenceIndexes, payload];
      return {
        ...state,
        currentSelectedEvidenceIndexes,
      };
    }

    case REDUX_ACTIONS.ROUND_END:
      return {
        ...initState,
      };

    case REDUX_ACTIONS.SET_GAME_MODE:
      return {
        ...state,
        gameMode: payload,
      };

    case REDUX_ACTIONS.SET_IS_TEXT_INTRO_PHASE:
      return {
        ...state,
        isTextIntroPhase: payload,
      };

    case REDUX_ACTIONS.SET_IS_TEXT_SCROLLER_AT_THE_BOTTOM:
      return {
        ...state,
        isTextScrollerAtTheBottom: payload,
      };

    case REDUX_ACTIONS.INCREMENT_ANSWERED_COUNT:
      return {
        ...state,
        answeredQuestionsCount: Math.min(state.answeredQuestionsCount + 1, QUESTIONS_PER_ROUND),
      };

    case REDUX_ACTIONS.INCREASE_ATTEMPT_COUNT: {
      const { attemptCount, failedQuestionsCount = 0, questionType } = state;
      const isLastMasteryAttemptFailed =
        questionType === QUESTION_TYPES.EVIDENCE ? attemptCount === 1 : attemptCount === 0;
      const newFailedQuestionsCount = isLastMasteryAttemptFailed ? failedQuestionsCount + 1 : failedQuestionsCount;
      return {
        ...state,
        attemptCount: state.attemptCount + 1,
        failedQuestionsCount: newFailedQuestionsCount,
      };
    }

    case REDUX_ACTIONS.SELECT_RESPONSE: {
      const { isCorrect, response } = payload;
      const selectedResponse = response;
      const { disabledPhrases, questionType, attemptCount } = state;
      let { currentSelectedEvidenceIndexes } = state;
      if (response instanceof Array) {
        currentSelectedEvidenceIndexes = currentSelectedEvidenceIndexes.filter(i => !disabledPhrases.includes(i));
      }
      if (isCorrect) {
        const isMasteryAttempt = questionType === QUESTION_TYPES.EVIDENCE ? attemptCount < 2 : attemptCount === 0;
        const correctAnswersCount = isMasteryAttempt ? state.correctAnswersCount + 1 : state.correctAnswersCount;
        return {
          ...state,
          gameMode: GAME_MODES.FEEDBACK_POSITIVE,
          correctAnswersCount,
          selectedResponse,
          currentSelectedEvidenceIndexes,
        };
      }
      return {
        ...state,
        gameMode: GAME_MODES.FEEDBACK_NEGATIVE,
        selectedResponse,
        currentSelectedEvidenceIndexes,
      };
    }

    case REDUX_ACTIONS.SET_PHRASES_DISABLED:
      return {
        ...state,
        disabledPhrases: [
          ...state.disabledPhrases,
          ...payload.phraseIndexes,
        ],
      };

    case REDUX_ACTIONS.CHANGE_SCREEN: {
      let { questionIndex, attemptCount, gameMode, questionType } = state;
      if (payload === SCREENS.DASHBOARD) {
        attemptCount = 0;
        questionType = questionIndex === 3 ? QUESTION_TYPES.INTEGRATION : questionType;
        gameMode = questionIndex === 3 ? GAME_MODES.MAKE_DECISION : gameMode;
        questionIndex = questionIndex === 3 ? questionIndex + 1 : questionIndex;
      }
      return {
        ...state,
        attemptCount,
        gameMode,
        questionIndex,
        questionType,
      };
    }

    case REDUX_ACTIONS.SET_SELECTED_INTEGRATION_OPTION_INDEX: {
      return {
        ...state,
        selectedIntegrationOptionIndex: payload,
      };
    }

    case REDUX_ACTIONS.SET_QUESTION_TYPE:
      return {
        ...state,
        questionType: payload.questionType,
      };

    case REDUX_ACTIONS.QUICK_ROUND:
      return {
        ...state,
        correctAnswersCount: payload.pass ? QUESTIONS_PER_ROUND : 0,
        questionIndex: QUESTIONS_PER_ROUND - 1,
      };

    default:
      return state;
  }
};

export default roundPlayReducer;
